import _ from "lodash";
/** external */
import React, { useMemo, useState } from "react";
import { Button, Dialog, IconButton, Tooltip, Badge } from "@toddle-edu/ds-web";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { compose } from "lib/apolloClient";
import { withRouter } from "react-router";
import {
  ChevronLeftOutlined,
  ChevronLeftCircleOutlined,
  TickCircleOutlined,
  AssessmentApplicableOutlined,
  CalculatorOutlined,
  BulletDotFilled,
  AssessmentOutlined,
  ChatTextLeftOutlined,
  ClockArrowsOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { Trans } from "react-i18next";
/** internal */
import { goToRelativeRoute } from "modules/Services";
import {
  isWorksheetWebView,
  sendMessageToParentApp,
  isStudentEndMode,
  getDurationForTimedTest,
} from "Worksheet/modules/WorksheetUtils";
import { getIsWorksheetMonitoringEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import AssessmentIcon from "UIComponents/AssessmentIcon";
import WorksheetTimer from "Worksheet/components/WorksheetTimer";

import {
  MODE_STUDENT_ATTEMPT,
  ASSESSMENT_TYPE_WORKSHEET,
  MODE_STUDENT_EVALUATE,
  WORKSHEET_MODES,
  MODE_STUDENT_VIEW,
} from "Worksheet/modules/WorksheetConstants";
import { getUserType } from "modules/CommonSelectors";
import { isSafeExamBrowser, isMobile } from "Utils/DeviceDetectHelpers";
import { getFormattedDuration } from "Utils/DateTimeHelpers";
/** relative */
import classes from "./Header.scss";
import {
  getStudentWorksheetDataFromEvents,
  getWorksheetTimerState,
} from "../../WorksheetTimer/WorksheetTimerUtils";

// Tooltip styling configuration
const styles = {
  overlayStyle: {
    maxWidth: 250,
  },
  badgeStyle: { position: "absolute", top: -4, right: -4 },
};

const MessageButton = ({
  buttonSize,
  unreadMessageCount,
  setShowMessagingPane,
}) => {
  const isWorksheetMonitoringEnabled = getIsWorksheetMonitoringEnabled();

  return (
    isWorksheetMonitoringEnabled && (
      <IconButton
        dsVersion="2.0"
        type="outlined"
        variant="neutral"
        size={buttonSize}
        onClick={() => setShowMessagingPane(prev => !prev)}
        icon={
          <>
            <ChatTextLeftOutlined />
            {unreadMessageCount > 0 && (
              <Badge
                size="xxx-small"
                dsVersion="2.0"
                showZero={false}
                style={styles.badgeStyle}
                value={unreadMessageCount}
              />
            )}
          </>
        }
        testId="worksheet-studentWorksheetSubmission-header-privateMessage"
      />
    )
  );
};

/**
 * Header component for Student Worksheet Submission
 * Handles different modes: ATTEMPT, EVALUATE, VIEW
 * Supports both old and new revamped UI designs
 * Manages timer, assessment tools, calculator, and submission flow
 */

const Header = props => {
  const {
    toggleWorksheetModal,
    toggleInstructionsPane,
    setIsFullScreenLE,
    headerLabel,
    contentType,
    onSubmitWork,
    setWorksheetWebViewRecentlySubmitted,
    mode,
    totalMarks,
    totalScore,
    handleWorksheetAssessmentToolClick,
    showWorksheetAssessmentTool,
    userType,
    t,
    setShowCalculator,
    registerTimeTrackingEvent,
    isTimedTestEnabled,
    isLockScreenEnabled,
    isSafeBrowserEnabled,
    isTestEnded,
    isOnScreenCalculator,
    timerComponentProps,
    closeSubmissionDate,
    unreadMessageCount,
    setShowMessagingPane,
    isSubmitted,
  } = props;

  const isStudentWorksheetWebView = isWorksheetWebView();
  const isWorksheetMonitoringEnabled = getIsWorksheetMonitoringEnabled();
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [
    showEndSafeExamBrowserDialog,
    setshowEndSafeExamBrowserDialog,
  ] = useState(false);

  const isInSafeExamBrowser = isSafeExamBrowser();
  const showStudentRevampedUI = isStudentEndMode(mode);

  const isMobileDevice = isMobile();

  const timedTestDuration = _.get(timerComponentProps, "timedTestDuration");
  const isWorksheetPaused = _.get(
    timerComponentProps,
    "isWorksheetPaused",
    false
  );
  const events = _.get(timerComponentProps, "events", []);

  /**
   * Considers reopen state and extra time got total duration calculation
   * For reopened tests, total reopen duration is used whihc already includes extra time
   */
  const { totalDuration = 0 } = getWorksheetTimerState({
    events,
    timedTestDuration,
    getTotalDuration: true,
  });

  const { extraTime } = getStudentWorksheetDataFromEvents(events);

  const durationToDisplay = getDurationForTimedTest({
    t,
    durationInSeconds: totalDuration,
  });
  const buttonSize =
    (showStudentRevampedUI && !isStudentWorksheetWebView) ||
    isWorksheetMonitoringEnabled
      ? "medium"
      : "large";

  const isInsideSafeExamBrowser = isSafeBrowserEnabled && isInSafeExamBrowser;
  const onViewInstructionClick = () => {
    setIsFullScreenLE(false);
    toggleInstructionsPane();
  };

  const toggleSubmitDialog = () => {
    setShowSubmitDialog(!showSubmitDialog);
  };

  const toggleEndSafeExamBrowserDialog = () => {
    setshowEndSafeExamBrowserDialog(!showEndSafeExamBrowserDialog);
  };

  /**
   * Handles worksheet submission - manages Safe Exam Browser and web view flows
   */
  const handleSubmitClick = async () => {
    setWorksheetWebViewRecentlySubmitted(true);
    await onSubmitWork({
      source: "MANUAL_SUBMIT_OLD",
    });
    if (isInsideSafeExamBrowser) {
      toggleEndSafeExamBrowserDialog();
    } else {
      toggleSubmitDialog();
    }
    if (isStudentWorksheetWebView) {
      sendMessageToParentApp("SUBMIT_WORKSHEET");
    } else {
      toggleWorksheetModal(false);
    }
  };

  /**
   * Handles back navigation - pauses timer for timed tests
   */
  const handleBackClick = () => {
    if (mode === MODE_STUDENT_ATTEMPT && isTimedTestEnabled) {
      registerTimeTrackingEvent({ eventType: "PAUSE" });
    }
    if (isStudentWorksheetWebView) {
      sendMessageToParentApp("SAVE_AND_EXIT");
    } else {
      toggleWorksheetModal(false);
    }
  };
  const toggleCalculatorModal = () => setShowCalculator(prev => !prev);
  const headerLabelClasses = classNames("heading-5", {
    [classes.truncateTitle]: true,
  });

  const showBackButton = !(
    (mode === MODE_STUDENT_ATTEMPT &&
      isTimedTestEnabled &&
      isLockScreenEnabled &&
      !isTestEnded) ||
    isInsideSafeExamBrowser
  );

  const marksContainerClass = classNames("text-label-3", {
    [classes.marksContainer]: isStudentWorksheetWebView,
  });

  const calculatorClass = classNames({
    [classes.calculator]: isStudentWorksheetWebView,
  });

  const subTitleClasses = classNames("text-body-3", classes.subTitleRevamp);

  const extraTimeContainerClasses = classNames({
    "text-label": true,
    [classes.extraTimeContainer]: true,
    [classes.separator]: true,
  });

  const containerClasses = classNames({
    [classes.container]: true,
    [classes.containerMobile]: isMobileDevice,
  });

  const worksheetTimerComponent = useMemo(() => {
    return isTimedTestEnabled ? (
      <WorksheetTimer
        {...timerComponentProps}
        isSafeBrowserEnabled={isSafeBrowserEnabled}
        isInSafeExamBrowser={isInSafeExamBrowser}
        toggleWorksheetModal={toggleWorksheetModal}
        worksheetTitle={headerLabel}
        onSubmitTimedWorksheet={onSubmitWork}
        closeSubmissionDate={closeSubmissionDate}
        setWorksheetWebViewRecentlySubmitted={
          setWorksheetWebViewRecentlySubmitted
        }
        isSubmitted={isSubmitted}
      />
    ) : null;
  }, [
    onSubmitWork,
    headerLabel,
    isInSafeExamBrowser,
    timerComponentProps,
    isTimedTestEnabled,
    toggleWorksheetModal,
    isSafeBrowserEnabled,
    closeSubmissionDate,
    setWorksheetWebViewRecentlySubmitted,
  ]);

  /**
   * Reusable Assessment Tools Button with tooltip
   */
  const AssessmentToolsButton = ({
    variant,
    type,
    _legacy_variant,
    onClick,
    t,
    disabled,
  }) => (
    <Tooltip
      tooltip={t("common:le_assessmentTools_label")}
      overlayStyle={styles.overlayStyle}
    >
      <IconButton
        dsVersion="2.0"
        variant={variant}
        type={type}
        size={buttonSize}
        _legacy_variant={_legacy_variant}
        _legacy_size="medium"
        icon={
          showStudentRevampedUI ? (
            <AssessmentOutlined />
          ) : (
            <AssessmentApplicableOutlined />
          )
        }
        onClick={onClick}
        disabled={disabled}
        testId="worksheet-studentWorksheetSubmission-header-assessmentTools"
      />
    </Tooltip>
  );

  /**
   * Renders timer with container styling - clones element to inject additional props
   */
  const renderTimerComponent = () => {
    const timerContainerClasses = classNames({
      [classes.timerContainer]: !showStudentRevampedUI,
    });
    return React.isValidElement(worksheetTimerComponent) ? (
      <div className={timerContainerClasses}>
        {React.cloneElement(worksheetTimerComponent, {
          isWorksheetSubmissionModal: true,
          showBackground: false,
          isSafeBrowserEnabled,
          isInSafeExamBrowser,
        })}
      </div>
    ) : null;
  };

  return (
    <div className={containerClasses}>
      {/* Left section: Navigation & Title */}
      <div className={classes.leftContainer}>
        <div className={classes.leftContainerSubText}>
          {showBackButton ? (
            <IconButton
              icon={
                <ChevronLeftOutlined
                  variant={"subtle"}
                  testId="worksheet-studentWorksheetSubmission-header-backButton"
                />
              }
              dsVersion="2.0"
              _legacy_variant="plain"
              _legacy_size="medium"
              variant="neutral"
              type="plain"
              size="medium"
              onClick={handleBackClick}
              testId="worksheet-studentWorksheetSubmission-header-backButton"
            />
          ) : null}
        </div>

        {/* Title and Subtitle Section */}
        {(!isWorksheetMonitoringEnabled || !isMobileDevice) && (
          <div className={classes.leftContainerDetails}>
            {!isMobileDevice ? (
              <AssessmentIcon
                assessmentType={ASSESSMENT_TYPE_WORKSHEET}
                contentType={contentType}
              />
            ) : null}
            <div className={classes.nameContainer}>
              <div className={headerLabelClasses}>{headerLabel}</div>

              {/* Subtitle with Timer + Marks (Revamped UI Only) */}
              {showStudentRevampedUI &&
              !isMobileDevice &&
              mode !== MODE_STUDENT_EVALUATE ? (
                <div className={subTitleClasses}>
                  {isTimedTestEnabled && durationToDisplay ? (
                    <>
                      {durationToDisplay}
                      <BulletDotFilled variant="subtle" size="xxx-small" />
                    </>
                  ) : null}
                  {t("worksheet:total_marks_with_count", { count: totalMarks })}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {mode === MODE_STUDENT_ATTEMPT ? (
        <div className={classes.rightContainer}>
          {/* Timer */}
          {!showStudentRevampedUI || isWorksheetMonitoringEnabled
            ? renderTimerComponent()
            : null}

          {/* Extra Time */}
          {extraTime > 0 && isWorksheetMonitoringEnabled && (
            <div className={extraTimeContainerClasses}>
              {!isMobileDevice && (
                <ClockArrowsOutlined variant="success" size="xxx-small" />
              )}
              <span>
                +
                {t("worksheet:time_in_min", {
                  count: _.toNumber(
                    getFormattedDuration({
                      duration: extraTime,
                      unit: "seconds",
                      durationFormat: "mm",
                      trim: true,
                    })
                  ),
                })}
              </span>
            </div>
          )}

          {/* Total Marks */}
          {!showStudentRevampedUI ? (
            <div className={marksContainerClass}>
              {t("worksheet:total_marks_with_count", {
                count: totalMarks,
              })}
            </div>
          ) : null}

          <MessageButton
            buttonSize={buttonSize}
            unreadMessageCount={unreadMessageCount}
            setShowMessagingPane={setShowMessagingPane}
          />

          {/* Calculator Button */}
          {isOnScreenCalculator ? (
            <div className={calculatorClass}>
              <IconButton
                dsVersion="2.0"
                type="outlined"
                variant="neutral"
                size={buttonSize}
                _legacy_size={"medium"}
                _legacy_variant={"outlined"}
                onClick={toggleCalculatorModal}
                icon={<CalculatorOutlined />}
                disabled={isWorksheetPaused}
                testId="worksheet-studentWorksheetSubmission-header-toggleCalculatorModal"
              />
            </div>
          ) : null}

          {/* Assessment Tools */}
          {showWorksheetAssessmentTool && (
            <AssessmentToolsButton
              _legacy_variant="outlined"
              variant="neutral"
              type="outlined"
              onClick={handleWorksheetAssessmentToolClick}
              t={t}
              disabled={isWorksheetPaused}
            />
          )}

          {/* Timer (Revamped UI) */}
          {showStudentRevampedUI && !isWorksheetMonitoringEnabled
            ? renderTimerComponent()
            : null}

          {/* Save & Exit Button  */}
          {!showStudentRevampedUI &&
          !isStudentWorksheetWebView &&
          showBackButton ? (
            <Button
              size="large"
              variant={"neutral"}
              dsVersion="2.0"
              type={"outlined"}
              _legacy_variant={"outlined"}
              _legacy_size={"medium"}
              icon={<ChevronLeftCircleOutlined />}
              onClick={handleBackClick}
              disabled={isWorksheetPaused}
              testId="worksheet-studentWorksheetSubmission-header-backButton"
            >
              {t("common:save_and_exit")}
            </Button>
          ) : null}

          {/* Submit Button - varies by UI type */}
          {showStudentRevampedUI || isInsideSafeExamBrowser ? (
            <Button
              size={buttonSize}
              variant={"destructive"}
              _legacy_variant={"destructive"}
              dsVersion="2.0"
              _legacy_size={"medium"}
              icon={<TickCircleOutlined />}
              onClick={
                isInsideSafeExamBrowser
                  ? toggleEndSafeExamBrowserDialog
                  : toggleSubmitDialog
              }
              disabled={isWorksheetPaused}
              testId="worksheet-studentWorksheetSubmission-header-toggleSEB"
              className={classes.submitButton}
            >
              {showStudentRevampedUI &&
              isMobileDevice &&
              !isInsideSafeExamBrowser
                ? t("common:submit")
                : t("worksheet:end_and_submit")}
            </Button>
          ) : (
            <Button
              size="large"
              variant={"progressive"}
              _legacy_variant={"progressive"}
              dsVersion="2.0"
              _legacy_size={"medium"}
              icon={<TickCircleOutlined />}
              onClick={toggleSubmitDialog}
              disabled={isWorksheetPaused}
              testId="worksheet-studentWorksheetSubmission-header-submitButton"
              className={classes.submitButton}
            >
              {t("common:submit")}
            </Button>
          )}
        </div>
      ) : null}

      {mode === MODE_STUDENT_EVALUATE ? (
        <div className={classes.rightContainer}>
          <div className={classes.scoreTagContainer}>
            <Trans
              i18nkey={"worksheet:score_with_count_and_totalCount"}
              count={totalScore}
              totalCount={totalMarks}
            >
              <span className="text-body-2">Score: </span>
              <span className="heading-5">{{ count: totalScore }}</span>
              <span className="text-body-2">/{{ totalCount: totalMarks }}</span>
            </Trans>
          </div>

          {showWorksheetAssessmentTool && (
            <AssessmentToolsButton
              _legcay_variant="plain-default"
              variant="neutral"
              type="plain"
              onClick={handleWorksheetAssessmentToolClick}
              t={t}
            />
          )}
        </div>
      ) : null}

      {mode === MODE_STUDENT_VIEW ? (
        <div className={classes.rightContainer}>
          <MessageButton
            buttonSize={buttonSize}
            unreadMessageCount={unreadMessageCount}
            setShowMessagingPane={setShowMessagingPane}
          />
          {showWorksheetAssessmentTool && (
            <AssessmentToolsButton
              _legcay_variant="plain-default"
              variant="neutral"
              type="plain"
              onClick={handleWorksheetAssessmentToolClick}
              t={t}
            />
          )}
        </div>
      ) : null}
      {showEndSafeExamBrowserDialog ? (
        <Dialog
          variant={"delete-dialog"}
          title={t("worksheet:submit_and_exit_seb")}
          message={t("worksheet:confirm_exit_seb")}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:exit")}
          onButtonOneClick={toggleEndSafeExamBrowserDialog}
          onButtonTwoClick={handleSubmitClick}
          onClose={toggleEndSafeExamBrowserDialog}
          testId="worksheet-studentWorksheetSubmission-header-exitDialog"
        />
      ) : null}
      {showSubmitDialog ? (
        <Dialog
          variant={"confirm-dialog"}
          title={t("common:submit_with_label", {
            label: t("common:worksheet"),
          })}
          message={t("worksheet:confirm_worksheet_submission")}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:confirm")}
          onButtonOneClick={toggleSubmitDialog}
          onButtonTwoClick={handleSubmitClick}
          onClose={toggleSubmitDialog}
          testId="worksheet-studentWorksheetSubmission-header-submitDialog"
        />
      ) : null}
    </div>
  );
};

const mapActionCreators = { goToRelativeRoute };

const mapStateToProps = state => {
  const curriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type"
  );
  const userType = getUserType(state);

  return { curriculumProgramType, userType };
};

Header.propTypes = {
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  totalMarks: PropTypes.number,
  totalScore: PropTypes.number,
  t: PropTypes.func,
  headerLabel: PropTypes.string,
  setIsFullScreenLE: PropTypes.func,
  curriculumProgramType: PropTypes.string,
  contentType: PropTypes.string,
  showWorksheetAssessmentTool: PropTypes.bool,
  userType: PropTypes.string,
  goToRelativeRoute: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool,
};

Header.defaultProps = {
  setWorksheetWebViewRecentlySubmitted: _.noop,
  isSubmitted: false,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  withRouter
)(Header);
